import React, { useEffect, useState } from 'react';
import { t } from 'i18next';
import { useFormik } from 'formik';
import { Link } from 'react-router-dom';
import { Filter, FilterContent, FilterInput, FilterSelectNative } from '@/components/custom/filter';
import { EmptyState } from '@/components/custom/empty-state';
import { LessonPlanRowMaterial } from './lesson-plan-row-material';
import { LessonPlanTreeItem } from './lesson-plan-tree-item';
import { useQuery } from 'jsonapi-react';
import { Loader } from '@/components/custom/loader';
import { Calendar } from 'lucide-react';
import { DashboardContent } from '@/layouts/dashboard';
import { Finder, FinderContent, FinderTree } from '@/components/custom/finder';
import { Table, TableBody, TableHeaderWrapper } from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { PATHS } from '@/routes/paths';
import usePlayfulTeacher from '@/hooks/usePlayfulTeacher';
import { TiaBanner } from '@/components/custom/tia-banner';
import { JOINHA } from '@/pages/dashboard/home-teacher/teacher-character';

// Fundo em gradiente do card do tema playful (mesmo usado no PlayfulCard).
const PLAYFUL_CARD_GRADIENT = 'linear-gradient(180deg, #F4FDFF 0%, #F2FCFF 14.65%, #FFFCF1 27.71%)';

const TABLE_HEAD = [{ label: 'Série e modalidade' }, { label: 'Turno', width: 76 }, { label: 'Mês', width: 76 }, { label: '', width: 130 }, { label: '', width: 40 }];

export function arraysAreEqual(array1, array2) {
  if (!array1 || !array2) return false;
  if (array1?.length !== array2?.length) {
    return false;
  }

  for (let i = 0; i < array1?.length; i++) {
    if (array1?.[i] !== array2?.[i]) {
      return false;
    }
  }

  return true;
}

const tabs = [
  {
    name: 'Plano de aulas',
    url: PATHS.LESSON_PLAN.START,
    slug: 'inicio',
    order: 1
  },
  {
    name: 'Favoritos',
    url: PATHS.LESSON_PLAN.FAVORITES,
    slug: 'favoritos',
    order: 2
  }
];

const quarter = [
  { id: 1, name: '1º trimestre' },
  { id: 2, name: '2º trimestre' },
  { id: 3, name: '3º trimestre' },
  { id: 4, name: '4º trimestre' }
];

const shifts = [
  { id: 1, name: 'Manhã' },
  { id: 2, name: 'Tarde' },
  { id: 3, name: 'Noite' },
  { id: 4, name: 'Integral' }
];

export function LessonPlan({ selectedTab, uri }) {
  const { data: stages, isLoading } = useQuery(selectedTab === 'inicio' ? '/educational_stages' : '/educational_stages?favorite=true');
  const playfulTeacher = usePlayfulTeacher();

  const [currentFolder, setCurrentFolder] = useState(null);
  const [idOpenedFolder, setIdOpenedFolder] = useState(null);
  const [idOpenedSubFolder, setIdOpenedSubFolder] = useState(null);

  const filters = useFormik({
    initialValues: {
      seachTerm: '',
      shift: 'blank',
      quarter: 'blank',
      year: 'blank'
    }
  });

  useEffect(() => {
    setIdOpenedFolder(null);
    setCurrentFolder(null);
    setIdOpenedSubFolder(null);
  }, [selectedTab]);

  const getQuarter = quarter => {
    switch (quarter) {
      case 'Q1':
        return '1º trimestre';
      case 'Q2':
        return '2º trimestre';
      case 'Q3':
        return '3º trimestre';
      case 'Q4':
        return '4º trimestre';
      default:
        return '';
    }
  };

  const filteredStages = stages
    ?.filter(stage => stage?.title?.toLowerCase()?.includes(filters.values.seachTerm) ?? true)
    ?.filter(stage => filters.values.quarter === 'blank' || stage?.['class-plans']?.some(item => getQuarter(item.quarter) === filters.values.quarter))
    ?.filter(stage => filters.values.shift === 'blank' || stage?.['class-plans']?.some(item => item.shift === filters.values.shift))
    ?.filter(stage => filters.values.year === 'blank' || stage?.year === filters.values.year);

  const handleClickFolder = folder => {
    setIdOpenedFolder(prev => (prev === folder.id ? prev : folder.id));
    setCurrentFolder(prev => (arraysAreEqual(prev, folder?.['class-plans']) ? prev : folder?.['class-plans']));
    setIdOpenedSubFolder(null);
  };

  const breadcrumbs = {
    title: 'Cursos e Aulas',
    nav: [
      {
        route: uri,
        name: 'Plano de Aulas',
        isActive: true
      }
    ]
  };

  const years = [...new Set(stages?.map(stage => stage.year))].sort((a, b) => a - b);

  // Aba ativa chanfrada (relevo 3D igual aos botões), mesmo padrão de course-tabs.jsx.
  const playfulTab = playfulTeacher
    ? 'data-[state=active]:bg-[var(--color-mission)] data-[state=active]:text-white data-[state=active]:font-extrabold data-[state=active]:shadow-[0_3px_0_var(--color-mission-dark)] data-[state=active]:-translate-y-px'
    : undefined;

  return (
    <DashboardContent
      variant="column"
      breadcrumbs={breadcrumbs}
    >
      {playfulTeacher && (
        <div className="relative mb-6 sm:mt-24">
          <TiaBanner
            className="h-full"
            image={JOINHA}
            imageAlt="Personagem do professor"
            imageHeight={215}
            imageOffset={200}
            bubbleCorner="tl"
            bubbleMaxWidth={500}
            text="Aqui estão seus planos de aula! 📚 Filtre por ano, trimestre ou turno e baixe o material pra sua próxima aula."
          />
        </div>
      )}

      <Tabs
        value={selectedTab}
        className="w-full"
      >
        <TabsList className={playfulTeacher ? 'mb-4 border border-neutral-lighter bg-white' : 'mb-4'}>
          {tabs.map(tab => (
            <TabsTrigger
              key={tab.slug}
              value={tab.slug}
              className={playfulTab}
              asChild
            >
              <Link to={tab.url}>{tab.name}</Link>
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {isLoading ? (
        <Loader />
      ) : (
        <>
          <Filter variant="plain">
            <FilterContent>
              <FilterInput
                label="Pesquisa"
                placeholder={t('textsCommon.research')}
                type="search"
                name="search"
                onChange={e => filters.setFieldValue('seachTerm', e.target.value)}
              />
              <FilterSelectNative
                blankLabel="Ano"
                label="Selecione o ano"
                value={filters.values.year}
                onChange={e => filters.setFieldValue('year', e.target.value)}
                icon={Calendar}
                options={years.map(year => ({
                  id: year,
                  name: year
                }))}
              />
              <FilterSelectNative
                blankLabel="Trimestre"
                label="Selecione um período"
                value={filters.values.quarter}
                onChange={e => filters.setFieldValue('quarter', e.target.value)}
                icon={Calendar}
                options={quarter.map(o => ({
                  id: o.name,
                  name: o.name
                }))}
              />
              <FilterSelectNative
                blankLabel="Selecione um turno"
                label="Selecione um turno"
                value={filters.values.shift}
                onChange={e => filters.setFieldValue('shift', e.target.value)}
                options={shifts.map(o => ({
                  id: o.name,
                  name: o.name
                }))}
              />
            </FilterContent>
          </Filter>

          <Finder
            className={playfulTeacher ? 'xl:min-h-[560px]' : undefined}
            style={playfulTeacher ? { background: PLAYFUL_CARD_GRADIENT } : undefined}
          >
            <FinderTree>
              {filteredStages?.map(folder => (
                <LessonPlanTreeItem
                  key={folder.id}
                  folder={folder}
                  isOpenFolder={idOpenedFolder === folder.id}
                  idOpenedSubFolder={idOpenedSubFolder}
                  currentFolder={currentFolder}
                  setCurrentFolder={setCurrentFolder}
                  setIdOpenedSubFolder={setIdOpenedSubFolder}
                  setIdOpenedFolder={setIdOpenedFolder}
                  onClick={() => handleClickFolder(folder)}
                />
              ))}

              {filteredStages?.length === 0 && (
                <div className="flex h-full items-center">
                  <EmptyState
                    type="data"
                    text="Sem dados para visualizar"
                    variant="plain"
                  />
                </div>
              )}
            </FinderTree>

            <FinderContent>
              {(!currentFolder || currentFolder?.length === 0) && (
                <EmptyState
                  type="select"
                  text="Selecione uma opção para visualizar"
                  variant="plain"
                  className="m-auto"
                />
              )}

              {currentFolder?.length > 0 && (
                <Table className="min-w-[500px] text-sm">
                  <TableHeaderWrapper tableHead={TABLE_HEAD} />

                  <TableBody>
                    {currentFolder?.map(item => (
                      <LessonPlanRowMaterial
                        key={item?.id}
                        material={item}
                      />
                    ))}
                  </TableBody>
                </Table>
              )}
            </FinderContent>
          </Finder>
        </>
      )}
    </DashboardContent>
  );
}
